import React from 'react';
import PropTypes from 'prop-types';
import lottie from 'lottie-web';
import {Code as Loader} from 'react-content-loader';
import {throttle} from '@learningblocks/exp-constants/lib/Utilities';

import style from './style.module.scss';

const LOTTIE_EVENTS = {
  ENTER_FRAME: 'enterFrame',
  COMPLETE: 'complete',
  DATA_READY: 'data_ready',
};

const LOAD_ANIMATION_CONFIG = {
  renderer: 'svg',
  renderderSettings: {
    preserveAspectRatio: 'xMinYMin slice',
  },
};

const EVENT_RESIZE = 'resize';

/**
 * Animation:
 * This component displays the actual animation/content of timer player
 */
export function Animation(props) {
  let element = React.useRef();

  const [anime, setAnime] = React.useState();
  const [loading, setLoading] = React.useState();

  const [height, setHeight] = React.useState('auto');
  const [width, setWidth] = React.useState('auto');

  const onResize = () => {
    throttle(() => setAnimeDimentions(), 15, 'animation');
  };

  React.useEffect(() => {
    if (props.animationData.isPath) setLoading(true);
    else {
      setLoading(false);
    }
  }, [props.animationData]);

  React.useEffect(() => {
    window.addEventListener(EVENT_RESIZE, onResize);
    return () => {
      window.removeEventListener(EVENT_RESIZE, onResize);
    };
  }, []);

  React.useEffect(() => {
    if (!loading) props.autoPlay();
  }, [loading]);

  const setAnimeDimentions = () => {
    if (anime) {
      const parentHeight = element.current.parentElement.clientHeight;
      const parentWidth = element.current.parentElement.clientWidth;
      setHeight(parentHeight.toString() + 'px');
      setWidth(parentWidth.toString() + 'px');
      if (parentHeight === 0) setHeight('auto');
      if (parentWidth === 0) setWidth('auto');
    }
  };

  const setAnimeFrame = (frame) => {
    anime.goToAndStop(frame, true);
  };

  const playAnime = () => {
    anime.play();
  };

  const listenFrameChange = ({currentTime}) => {
    props.onChangeFrame(currentTime);
  };

  const animationLoaded = () => {
    setLoading(false);
  };

  const addEventListeners = (anime) => {
    anime.addEventListener(LOTTIE_EVENTS.ENTER_FRAME, listenFrameChange);
    if (anime.animationData.isPath)
      anime.addEventListener(LOTTIE_EVENTS.DATA_READY, animationLoaded);
  };

  React.useEffect(() => {
    if (!anime) {
      let anim = getLottieInstance(props.animationData, element.current);
      addEventListeners(anim);
      anim.onComplete = () => {
        props.onPlaybackComplete();
      };
      props.setAnimeRange(anim.firstFrame, anim.totalFrames - 1);
      setAnime(anim);
      setAnimeDimentions();
    }
  }, []);

  React.useEffect(() => {
    if (anime) {
      setAnimeFrame(props.frame);
      !props.isPaused && playAnime();
    }
  }, [props.frame]);

  React.useEffect(() => {
    if (anime) {
      if (props.isPaused) {
        setAnimeFrame(props.frame);
      } else {
        playAnime();
      }
    }
  }, [props.isPaused]);

  return (
    <div className={style.animation}>
      {loading ? (
        <Loader style={{margin: '16px'}} />
      ) : (
        <div
          style={{
            height: height,
            width: width,
            display: 'flex',
          }}
          ref={element}
        />
      )}
    </div>
  );
}

Animation.propTypes = {
  /**
   * onPlaybackComplete is used to detect the animation is completed or not
   */
  onPlaybackComplete: PropTypes.func,
  /**
   * onChangeFrame is function used to change the frame of animation
   */
  onChangeFrame: PropTypes.func,
  /**
   * Animation data provided
   */
  animationData: PropTypes.shape({
    /**
     * Array of animation frames
     */
    animation: PropTypes.array,
    /**
     * isPath is used to identify wheather the data is given as path or file
     */
    isPath: PropTypes.bool,
  }),
  /**
   * Function to set the animation range from min to max
   */
  setAnimeRange: PropTypes.func,
  /**
   * CurrentFrame stores the data of the animation shows at the moment
   */
  frame: PropTypes.number,
  /**
   * It is used to understand wheather the animation is running or not
   */
  isPaused: PropTypes.bool,
  /**
   * function to play animation when it is loaded
   */
  autoPlay: PropTypes.func,
};

/**
 * Function to create and instance of Animation
 */
function getLottieInstance(params, container) {
  if (params.isPath) {
    return lottie.loadAnimation({
      ...LOAD_ANIMATION_CONFIG,
      loop: false,
      autoplay: false,
      container: container,
      path: params.animation,
    });
  } else {
    return lottie.loadAnimation({
      ...LOAD_ANIMATION_CONFIG,
      loop: false,
      autoplay: false,
      container: container,
      animationData: params.animation,
    });
  }
}
